import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Database, AlertTriangle, FileText, Trash2, Inbox,
} from "lucide-react";

/**
 * v1.2 PR-C — GDPR & Privacy (consolidated).
 *
 * Folds the previously-separate `/settings/data-retention` and
 * `/admin/erasure-requests` pages into a single tab with anchored sections
 * (#retention, #erasure).
 *
 * Legacy URLs redirect via App.js.
 */

const SECTIONS = [
  { id: "retention", label: "Data retention",  icon: Database },
  { id: "erasure",   label: "Erasure queue",   icon: Inbox    },
];

function useScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    const t = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 50);
    return () => window.clearTimeout(t);
  }, [hash]);
}

function SectionSubnav({ sections }) {
  const { hash } = useLocation();
  const nav = useNavigate();
  const active = (hash || `#${sections[0].id}`).replace(/^#/, "");
  return (
    <nav className="sticky top-16 z-10 flex flex-wrap gap-1.5 bg-surface/80 dark:bg-navy-900/80 backdrop-blur-sm py-2 -mx-1 px-1 rounded-lg"
         data-testid="section-subnav">
      {sections.map((s) => {
        const Icon = s.icon;
        const isActive = active === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => nav(`#${s.id}`, { replace: true })}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
              isActive
                ? "bg-orange-500 text-white border-orange-500"
                : "bg-white dark:bg-navy-800 text-navy-700 dark:text-navy-200 border-navy-200 dark:border-navy-700 hover:border-orange-300"
            }`}
            data-testid={`subnav-${s.id}`}
          >
            <Icon className="w-3.5 h-3.5" /> {s.label}
          </button>
        );
      })}
    </nav>
  );
}

export default function SettingsGdprPrivacy() {
  useScrollToHash();
  return (
    <SettingsLayout>
      <div className="space-y-6" data-testid="settings-gdpr-privacy-page">
        <div>
          <div className="eb-overline mb-1">Module 14 · GDPR</div>
          <h1 className="text-3xl font-semibold text-navy-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-orange-500" /> GDPR &amp; privacy
          </h1>
          <p className="text-sm text-navy-500 dark:text-navy-300 mt-1 max-w-2xl">
            Configure retention windows for personal data and review/execute visitor-initiated
            right-to-erasure requests (Article 17).
          </p>
        </div>
        <SectionSubnav sections={SECTIONS} />
        <section id="retention" className="scroll-mt-24" data-testid="section-retention">
          <RetentionSection />
        </section>
        <div className="h-px bg-navy-100 dark:bg-navy-700" />
        <section id="erasure" className="scroll-mt-24" data-testid="section-erasure">
          <ErasureSection />
        </section>
      </div>
    </SettingsLayout>
  );
}

// ============================================================================
// Retention section — verbatim port of SettingsDataRetention body.
// ============================================================================

const CLASSES = [
  { key: "default", label: "Default (fallback)", helpDays: "Used when a per-class value is unset." },
  { key: "visits", label: "Visits", helpDays: "Visitor sign-in/out records." },
  { key: "notifications_log", label: "Notifications log", helpDays: "Email/Slack/Teams send history." },
  { key: "audit_log", label: "Audit log", helpDays: "Floor: 2555 days (~7 years). UK data protection minimum." },
  { key: "watchlist_hits", label: "Watchlist hits", helpDays: "Discreet block events." },
  { key: "evacuation_attendances", label: "Evacuation attendance", helpDays: "Per-event sign-in snapshots." },
];
const ACTION_CLASSES = ["visits", "notifications_log", "watchlist_hits", "evacuation_attendances"];

function RetentionSection() {
  const [policy, setPolicy] = useState({});
  const [action, setAction] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/orgs/me").then(({ data }) => {
      setPolicy({ default: data.retention_days || 90, ...(data.retention_policy || {}) });
      setAction(data.retention_action || {});
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const setDays = (k, v) => setPolicy({ ...policy, [k]: v ? Number(v) : null });
  const setAct = (k, v) => setAction({ ...action, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/orgs/me/retention-policy", {
        retention_policy: policy,
        retention_action: action,
      });
      toast.success("Retention policy saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="data-retention-page">
      <div>
        <h2 className="text-xl font-semibold text-navy-900 dark:text-white flex items-center gap-2">
          <Database className="w-5 h-5 text-orange-500" /> Data retention
        </h2>
        <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">
          Configure how long each class of personal data is kept before it is anonymised or hard-deleted.
          Audit log retention has a hard <strong>2,555-day floor</strong> (UK data protection / employment law).
        </p>
      </div>
      <div className="rounded-2xl bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 p-5 space-y-4">
        {CLASSES.map((c) => {
          const isAudit = c.key === "audit_log";
          return (
            <div key={c.key} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start" data-testid={`retention-row-${c.key}`}>
              <div>
                <div className="font-semibold text-navy-900 dark:text-white flex items-center gap-2">
                  {isAudit && <ShieldCheck className="w-4 h-4 text-warning-600" />}
                  {c.label}
                </div>
                <div className="text-xs text-navy-500 dark:text-navy-300 mt-0.5">{c.helpDays}</div>
              </div>
              <div>
                <label className="text-xs text-navy-600 dark:text-navy-300">Retention (days)</label>
                <input type="number" min={isAudit ? 2555 : 1} max={3650}
                  value={policy[c.key] ?? ""} onChange={(e) => setDays(c.key, e.target.value)}
                  placeholder={isAudit ? "2555" : "(default)"}
                  className="w-full mt-1 px-3 py-2 border border-navy-200 dark:border-navy-700 dark:bg-navy-700 dark:text-navy-100 rounded-lg"
                  data-testid={`retention-days-${c.key}`} />
                {isAudit && (policy[c.key] ?? 0) < 2555 && (
                  <div className="text-xs text-danger-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Floor 2555 days
                  </div>
                )}
              </div>
              <div>
                {ACTION_CLASSES.includes(c.key) && (
                  <>
                    <label className="text-xs text-navy-600 dark:text-navy-300">When older than this</label>
                    <select value={action[c.key] ?? "anonymise"} onChange={(e) => setAct(c.key, e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-navy-200 dark:border-navy-700 dark:bg-navy-700 dark:text-navy-100 rounded-lg"
                      data-testid={`retention-action-${c.key}`}>
                      <option value="anonymise">Anonymise (preserves stats)</option>
                      <option value="hard_delete">Hard delete</option>
                    </select>
                  </>
                )}
                {isAudit && (
                  <div className="text-xs text-navy-500 dark:text-navy-300 mt-5">Audit log: hard delete only.</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Button onClick={save} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-retention-btn">
        {busy ? "Saving…" : "Save retention policy"}
      </Button>
      <a href="/api/orgs/me/processing-record.pdf" target="_blank" rel="noreferrer"
         className="inline-flex items-center gap-2 text-sm text-orange-600 hover:underline ml-3" data-testid="download-roap-btn">
        <FileText className="w-4 h-4" /> Download ICO Article-30 Processing Record (PDF)
      </a>
    </div>
  );
}

// ============================================================================
// Erasure section — verbatim port of ErasureRequests body.
// ============================================================================

function fmt(iso) { return iso ? new Date(iso).toLocaleString() : "—"; }
function tone(status) {
  return status === "executed" ? "bg-danger-50 text-danger-700"
    : status === "cancelled" ? "bg-navy-50 text-navy-500"
    : status === "cooling_off" ? "bg-warning-50 text-warning-700"
    : status === "pending_confirm" ? "bg-navy-50 text-navy-700"
    : "bg-navy-50 text-navy-700";
}

function ErasureSection() {
  const [rows, setRows] = useState([]);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/erasure-requests");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const execute = async (id) => {
    if (!window.confirm("Execute hard-delete now? This cannot be undone.\n\nNote: only available after the 7-day cool-down has elapsed.")) return;
    try {
      await api.post(`/admin/erasure-requests/${id}/execute`);
      toast.success("Erasure executed");
      await refresh();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 425 && det?.error === "erasure_cooling_off") {
        toast.error(`Cool-down active — ${det.days_remaining} day(s) remaining. Use "Force execute" if it cannot wait.`);
      } else {
        toast.error(formatApiError(e));
      }
    }
  };
  const forceExecute = async (id) => {
    const justification = window.prompt(
      "Force-execute bypasses the 7-day GDPR cool-down. " +
      "Provide a justification (≥40 characters; e.g. court order, urgent legal request):",
    );
    if (!justification) return;
    if (justification.length < 40) {
      return toast.error(`Justification too short — need ≥40 characters (got ${justification.length}).`);
    }
    try {
      await api.post(`/admin/erasure-requests/${id}/force-execute`, { justification });
      toast.success("Force-executed (audit row written)");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Cancel this erasure request?")) return;
    try {
      await api.post(`/admin/erasure-requests/${id}/cancel`);
      toast.success("Cancelled");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="erasure-page">
      <div>
        <h2 className="text-xl font-semibold text-navy-900 dark:text-white flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-danger-500" /> Right-to-erasure queue
        </h2>
        <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">
          Visitor-initiated GDPR Article 17 requests. After confirmation a 7-day
          cool-down applies before automatic execution. You may execute immediately
          or cancel.
        </p>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-navy-200 dark:border-navy-700 p-10 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-success-600 mb-2" />
          <div className="text-navy-700 dark:text-navy-100 font-medium">No erasure requests pending.</div>
        </div>
      ) : (
        <div className="rounded-2xl bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-navy-50 dark:bg-navy-700 text-navy-600 dark:text-navy-200 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Identifier (hashed)</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Requested</th>
                <th className="px-4 py-3 text-left">Confirmed</th>
                <th className="px-4 py-3 text-left">Scheduled</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100 dark:divide-navy-700">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`erasure-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-[11px] text-navy-700 dark:text-navy-200 truncate max-w-[220px]">{r.identifier_hash}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${tone(r.status)}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-navy-600 dark:text-navy-300">{fmt(r.requested_at)}</td>
                  <td className="px-4 py-3 text-xs text-navy-600 dark:text-navy-300">{fmt(r.confirmed_at)}</td>
                  <td className="px-4 py-3 text-xs text-navy-600 dark:text-navy-300">{fmt(r.scheduled_execution_at)}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {r.status === "cooling_off" && (
                      <Button size="sm" variant="outline" onClick={() => execute(r.id)} data-testid={`erasure-execute-${r.id}`}>Execute now</Button>
                    )}
                    {r.status === "cooling_off" && (
                      <Button size="sm" variant="ghost"
                              className="text-danger-600 hover:bg-danger-50"
                              onClick={() => forceExecute(r.id)}
                              data-testid={`erasure-force-${r.id}`}>
                        Force execute
                      </Button>
                    )}
                    {(r.status === "cooling_off" || r.status === "pending_confirm") && (
                      <Button size="sm" variant="ghost" onClick={() => cancel(r.id)} data-testid={`erasure-cancel-${r.id}`}>Cancel</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
